import { BillingProvider } from "@growthzilla/billingui";
import { useAuth } from "@hooks/useAuth";
import useDialog from "@hooks/useDialog";
import useGzLog from "@hooks/useGzLog";
import { PlanProperties } from "@hooks/usePlans/types";
import { useUserSubscriptions } from "@hooks/useUserSubscriptions";
import { env } from "@src/env.mjs";
import React from "react";

export default function SignageBillingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { session, organization, user } = useAuth();
  const { showSuccessSnackbar, showErrorSnackbar } = useDialog();
  const { addOn, planProperties, subscription } = useUserSubscriptions()
  const { log } = useGzLog();
  const addOnProperties = JSON.parse(addOn?.properties || "{}") as PlanProperties;
  const addOnPropertiesWithCost = [
    {
      label: "Screen",
      count: addOnProperties.entitled_screens ?? 0,
      unitCost: planProperties.addon_screen_cost ?? 0,
      subscriptionId: subscription?.id ?? 0,
    },
    {
      label: "Music Integration",
      count: addOnProperties.entitled_music_zones ?? 0,
      unitCost: planProperties.addon_music_cost ?? 0,
      subscriptionId: subscription?.id ?? 0,
    },
  ]

  if (!user || !session) return null;

  const profile = organization?.profile

  return (
    <BillingProvider
      isSystemAdmin={user?.system_role === "admin"}
      proxyServerUrl={env.NEXT_PUBLIC_PROXY_SERVER_URL}
      stripePublishableKey={env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY}
      addOns={addOnPropertiesWithCost}
      messageEventHandler={(type, msg) => {
        switch (type) {
          case "success":
            showSuccessSnackbar(msg);
            log({
              eventtype: "BillingSuccess",
              name: "BillingSuccess",
              payload: {
                message: msg,
                userId: user.id,
                organizationId: organization?.id,
              },
            });
            break;
          case "error":
            showErrorSnackbar(msg);
            log({
              eventtype: "BillingError",
              name: "BillingError",
              payload: {
                message: msg,
                userId: user.id,
                organizationId: organization?.id,
              },
            });
            break;
        }
      }}
      systemId="signage-studio"
      impersonatingUser={{
        id: profile?.id ?? "",
        fullname: profile?.email?.split("@")?.[0] ?? "",
        email: profile?.email ?? "",
        role: profile?.system_role ?? "member",
        permissions: user?.organizations?.map((org) => org.id) ?? [],
      }}
      user={{
        id: user.id,
        fullname: user.email?.split("@")?.[0] ?? "",
        email: user.email ?? "",
        role: user.system_role ?? "member",
        permissions: user?.organizations?.map((org) => org.id) ?? [],
      }}
      userBusinesses={[
        {
          active: 1,
          id: organization?.id ?? "",
          name: organization?.name ?? "",
          status: "ACTIVE",
        },
      ]}
      proxyAuthHeaders={{
        Authorization: `Bearer ${session.access_token}`,
        'user-id': user.id,
        'business-id': organization?.id,
      }}
    >
      {children}
    </BillingProvider>
  );
}
